import { Box, Stack } from "@mui/system";
import React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import bannerTopPic from "../../../images/gh_top_banner_a.png";

import "./Banner.css";
import "./CircleRipple.css";
import "./ClockAnimate.css";

import {
  Button,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PlayCircle, Search } from "@mui/icons-material";
import { keywordApi } from "../../API/Api";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import VideoModal from "./VideoModal";
import InteractiveBanner from "./InteractiveBanner";
import { tokens } from "../../../store/tokens";

const heroContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const primaryButtonSx = {
  borderRadius: `${tokens.button.radius}px`,
  height: { xs: tokens.button.height.md, sm: tokens.button.height.lg },
  px: `${tokens.button.paddingX.lg}px`,
  fontFamily: tokens.font.body,
  fontWeight: tokens.type.button.fontWeight,
  fontSize: tokens.button.fontSize.lg,
  textTransform: tokens.type.button.textTransform,
  bgcolor: tokens.bg.brand,
  color: tokens.color.white,
  boxShadow: "none",
  transition: "background-color 0.2s ease",
  "&:hover": { bgcolor: tokens.bg.brandDark },
};

const Banner = () => {
  const navigate = useNavigate();
  const [keywordData1st, setKeywordData1st] = useState([]);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("");

  const [openVideo, setOpenVideo] = useState(false);
  const handleOpenVideo = () => setOpenVideo(true);
  const handleCloseVideo = () => setOpenVideo(false);

  const handleClickSearch = (value) => setSearch(value);

  useEffect(() => {
    fetch(keywordApi)
      .then((res) => res.json())
      .then((result) => {
        setKeywordData1st(result.slice(0, 4));
      });
  }, []);

  const handleChange = (event) => setSearch(event.target.value);
  const handleTypeChange = (event) => setSearchType(event.target.value);

  // Bug fix: an empty category produced "/search//text", which matches no
  // route, and the text wasn't URL-encoded. Same logic as the header search.
  const handleSearchSubmit = () => {
    const query = search.trim();
    if (!query) return;
    navigate(`/search/${searchType || "all"}/${encodeURIComponent(query)}`);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    draggable: false,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  // NOTE: this effect drives an analog clock (.hour/.minute/.second) that may
  // live inside InteractiveBanner — left untouched, it safely no-ops if those
  // elements aren't present.
  useEffect(() => {
    const interval = setInterval(() => {
      const hourHand = document.querySelector(".hour");
      const minuteHand = document.querySelector(".minute");
      const secondHand = document.querySelector(".second");
      if (!hourHand || !minuteHand || !secondHand) return;

      const date = new Date();
      const hours = ((date.getHours() + 11) % 12) + 1;
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();

      const hour = hours * 30;
      const minute = minutes * 6;
      const second = seconds * 6;

      hourHand.style.transform = `rotate(${hour}deg)`;
      minuteHand.style.transform = `rotate(${minute}deg)`;
      secondHand.style.transform = `rotate(${second}deg)`;

      second === 0
        ? secondHand.classList.add("hide-transition")
        : secondHand.classList.remove("hide-transition");

      let hourOffsetSign = hour > 135 && hour < 315 ? "-" : "";
      let minuteOffsetSign = minute > 135 && minute < 315 ? "-" : "";
      let secondOffsetSign = second > 135 && second < 315 ? "-" : "";

      hourHand.style.boxShadow = `${hourOffsetSign}6px ${hourOffsetSign}6px 6px #b8b9be`;
      minuteHand.style.boxShadow = `${minuteOffsetSign}6px ${minuteOffsetSign}6px 6px #b8b9be`;
      secondHand.style.boxShadow = `${secondOffsetSign}6px ${secondOffsetSign}6px 6px #b8b9be`;
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Stack
      sx={{
        width: "100%",
        maxWidth: "100%",
        position: "relative",
        // Reduced top margin on small devices (was 80px on phones).
        marginTop: { xs: 0, sm: "8px", md: "20px", lg: 0 },
      }}
    >
      <VideoModal open={openVideo} handleClose={handleCloseVideo} />

      <Box sx={{ display: { xs: "none", lg: "block" } }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <InteractiveBanner
            sx={{
              width: "100%",
              position: "relative",
              display: { xs: "none", lg: "block" },
            }}
          />
        </motion.div>
      </Box>

      <Stack
        component={motion.div}
        variants={heroContainer}
        initial="hidden"
        animate="visible"
        justifyContent={{ xs: "flex-start", lg: "center" }}
        spacing={{ xs: 2, md: 2.5 }}
        sx={{
          // 5% + 90% on phones, 8% + 84% on tablets: symmetric gutters
          // (md used to be 8% + 90%, pushing content toward the right edge).
          marginLeft: { xs: "5%", md: "8%" },
          width: { xs: "90%", md: "84%", lg: "53%" },
          minWidth: 0,
          minHeight: { xs: "auto", lg: "60vh" },
          py: { xs: 2, sm: 3, lg: 0 },
          position: { xs: "relative", lg: "absolute" },
          // offsets only apply to the overlay layout on lg+; on smaller
          // screens they used to nudge the block down by 10px for no reason
          top: { xs: "auto", lg: 10 },
          bottom: { xs: "auto", lg: 5 },
        }}
      >
        <Stack component={motion.div} variants={heroItem} spacing={1.5}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.75,
              py: 0.6,
              borderRadius: `${tokens.radius.pill}px`,
              bgcolor: tokens.color.tealLight,
              color: tokens.color.teal,
              fontFamily: tokens.font.body,
              fontWeight: 700,
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              width: "fit-content",
              maxWidth: "100%",
            }}
          >
            Where Skills Meet Opportunity.
          </Box>
          <Typography
            sx={{
              fontFamily: tokens.font.heading,
              fontWeight: tokens.type.h1.fontWeight,
              letterSpacing: tokens.type.h1.letterSpacing,
              fontSize: { xs: "1.75rem", sm: "2.3rem", lg: tokens.type.h1.fontSize },
              lineHeight: 1.15,
              color: tokens.textOn.light.primary,
              overflowWrap: "anywhere",
            }}
          >
            Work. Hire. Connect. Grow.
          </Typography>
        </Stack>

        <Stack component={motion.div} variants={heroItem} spacing={0.5}>
          <Typography
            sx={{
              fontFamily: tokens.font.body,
              fontWeight: 500,
              fontSize: { xs: "0.9rem", sm: "0.95rem", md: tokens.type.body1.fontSize },
              lineHeight: { xs: 1.5, md: 1.6 },
              color: tokens.textOn.light.secondary,
            }}
          >
            From finding the perfect expert to selling your own skills, Songjukti makes remote work simple. Connect with trusted professionals, explore services, collaborate on projects and unlock opportunities across Bangladesh and beyond.
          </Typography>
        </Stack>

        {/* Unified, responsive search bar — sized to the token input spec */}
        <Paper
          component={motion.div}
          variants={heroItem}
          whileHover={{ boxShadow: tokens.shadow.lg }}
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 1.25, sm: 0.75 },
            pl: { sm: 2.5 },
            // borderRadius: { xs: `${tokens.radius.lg}px`, sm: `${tokens.radius.pill}px` },
            borderRadius: { xs: `${tokens.radius.lg}px`, sm: 1 },
            border: "1px solid",
            borderColor: tokens.input.border,
            bgcolor: tokens.input.bg,
            boxShadow: tokens.shadow.md,
            transition: "box-shadow 0.3s ease",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.25, sm: 1 }}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            {/* autoFocus removed: on phones it popped the keyboard open on
                page load and covered the hero. */}
            <TextField
              variant="standard"
              fullWidth
              value={search}
              onChange={handleChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search for a skill, service or freelancer…"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontFamily: tokens.font.body,
                  // 16px minimum on phones stops iOS Safari zooming on focus
                  fontSize: { xs: "1rem", sm: tokens.input.fontSize.lg },
                },
              }}
              sx={{ flex: 1.6, minWidth: 0, px: { xs: 1, sm: 0 } }}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", sm: "block" }, my: 0.5, borderColor: tokens.color.line }}
            />

            <FormControl
              variant="standard"
              sx={{
                minWidth: { xs: "100%", sm: 130, md: 160 },
                px: { xs: 1, sm: 1 },
              }}
            >
              <Select
                value={searchType}
                onChange={handleTypeChange}
                displayEmpty
                disableUnderline
                sx={{
                  fontFamily: tokens.font.body,
                  fontSize: tokens.input.fontSize.lg,
                  fontWeight: 500,
                  color: tokens.color.ink,

                  // Prevent background color changes
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "transparent",
                  },
                  "& .MuiSelect-select:focus": {
                    backgroundColor: "transparent",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      // Optional: keeps the dropdown itself normal
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: "transparent",
                      },
                      "& .MuiMenuItem-root.Mui-selected:hover": {
                        backgroundColor: "transparent",
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <span style={{ color: tokens.color.inkFaint }}>All categories</span>
                </MenuItem>
                <MenuItem value="job">Job</MenuItem>
                <MenuItem value="service">Service</MenuItem>
                <MenuItem value="freelancer">Freelancer</MenuItem>
                <MenuItem value="agency">Agency</MenuItem>
              </Select>
            </FormControl>

            <Button
              component={motion.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              variant="contained"
              onClick={handleSearchSubmit}
              startIcon={<Search />}
              sx={{ ...primaryButtonSx, flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
            >
              Search
            </Button>
          </Stack>
        </Paper>

        {keywordData1st.length > 1 && (
          // `spacing` removed: with flexWrap it adds stray margins on wrapped
          // rows; the gap below already spaces the chips evenly.
          <Stack
            component={motion.div}
            variants={heroItem}
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            sx={{ gap: { xs: "6px", sm: "8px" } }}
          >
            <Typography
              sx={{ fontFamily: tokens.font.body, color: tokens.textOn.light.secondary, fontWeight: 600, fontSize: tokens.type.subtitle2.fontSize }}
            >
              Trending:
            </Typography>
            {keywordData1st?.map((element) => (
              <Button
                key={element.title}
                component={motion.button}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "capitalize",
                  fontFamily: tokens.font.body,
                  fontSize: "0.8rem",
                  borderRadius: `${tokens.radius.pill}px`,
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  maxWidth: "100%",
                  color: tokens.color.ink,
                  borderColor: tokens.color.line,
                  backgroundColor: tokens.color.surfaceMuted,
                  transition: "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
                  "&:hover": {
                    backgroundColor: tokens.color.tealLight,
                    borderColor: tokens.color.teal,
                    color: tokens.color.teal,
                  },
                }}
                onClick={() => handleClickSearch(element.title)}
              >
                {element.title}
              </Button>
            ))}
          </Stack>
        )}

        <Stack component={motion.div} variants={heroItem} direction="row" spacing={2} alignItems="center">
          <Box sx={{ border: "1px solid", borderColor: tokens.color.line, borderRadius: "50px", flexShrink: 0 }}>
            <IconButton
              className="circle-ripple"
              size="large"
              onClick={handleOpenVideo}
              aria-label="Watch demo"
              sx={{ transition: "transform 0.2s ease", "&:hover": { transform: "scale(1.08)" } }}
            >
              <PlayCircle fontSize="inherit" sx={{ color: tokens.color.teal }} />
            </IconButton>
          </Box>
          <Stack>
            <Typography sx={{ fontFamily: tokens.font.body, fontWeight: 600, fontSize: "1rem", color: tokens.color.teal }}>
              Watch demo
            </Typography>
            <Typography sx={{ fontFamily: tokens.font.body, fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.9rem" }, color: tokens.textOn.light.secondary }}>
              Get started in minutes
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Banner;